import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { signup, validateStrongPassword } from "../Functions/firebaseFunction";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/Authcontext/AuthContext";
import useStore from "../store/useStore";
import {
  User,
  Mail,
  Phone,
  Lock,
  Eye,
  EyeOff,
  ArrowRight,
  AlertCircle,
  ShieldCheck,
  Check,
  CheckCircle,
  Bell,
  TrendingUp,
  Wrench,
  Calendar,
} from "lucide-react";
import { motion } from "framer-motion";

const SignUp = () => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    countryCode: "+1",
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { currentUser, loading: authLoading } = useAuth();
  const [checkingAuth, setCheckingAuth] = useState(true);

  useEffect(() => {
    let isMounted = true;
    const checkUser = async () => {
      if (authLoading) return;

      if (currentUser) {
        try {
          const userRef = doc(db, "vendorUsers", currentUser.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            useStore.getState().setSessionContext({ vendorId: currentUser.uid, userRole: "vendor" });
            if (!userData.accountSetupCompleted) {
              navigate("/account-setup", { replace: true });
              return;
            }
            navigate("/dashboard", { replace: true });
            return;
          }
        } catch (e) {
          console.error(e);
        }
      }
      if (isMounted) setCheckingAuth(false);
    };

    checkUser();
    return () => { isMounted = false; };
  }, [currentUser, authLoading, navigate]);

  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "firstName": if (!value.trim()) error = "First name is required"; break;
      case "lastName": if (!value.trim()) error = "Last name is required"; break;
      case "email":
        if (!value.trim()) error = "Email is required";
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) error = "Enter a valid email";
        break;
      case "mobile":
        if (!value.trim()) error = "Mobile is required";
        else if (!/^[0-9]{10}$/.test(value)) error = "Enter a valid 10-digit number";
        break;
      case "countryCode": if (!value.trim()) error = "Required"; break;
      case "password": error = validateStrongPassword(value); break;
      case "confirmPassword":
        if (!value) error = "Please confirm your password";
        else if (value !== formData.password) error = "Passwords do not match";
        break;
      default: break;
    }
    return error;
  };

  const handleInputChange = (name, value) => {
    if (name === "mobile") {
      setFormData({ ...formData, mobile: value.replace(/\D/g, "").slice(0, 10) });
      if (errors.mobile) setErrors({ ...errors, mobile: "" });
      return;
    }
    if (name === "countryCode") {
      setFormData({ ...formData, countryCode: value.replace(/[^+\d]/g, "").slice(0, 5) });
      if (errors.countryCode) setErrors({ ...errors, countryCode: "" });
      return;
    }
    setFormData({ ...formData, [name]: value });
    if (errors[name]) setErrors({ ...errors, [name]: "" });
  };

  const handleInputBlur = (name, value) => {
    const error = validateField(name, value);
    setErrors({ ...errors, [name]: error });
  };

  const validateForm = () => {
    const newErrors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) newErrors[key] = error;
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);
    setErrors({});
    try {
      const result = await signup(
        formData.email, formData.password,
        formData.firstName, formData.lastName,
        formData.mobile, formData.countryCode
      );
      if (!result.success) throw new Error(result.message);

      // Store session context for newly created vendor
      useStore.getState().setSessionContext({
        vendorId: result.user.uid,
        userRole: "vendor",
      });

      navigate("/account-setup", { replace: true });
    } catch (error) {
      setErrors({ general: error.message || "Failed to create account. Please try again." });
    } finally {
      setLoading(false);
    }
  };

  const passLength = formData.password.length >= 8;
  const passUpper = /[A-Z]/.test(formData.password);
  const passNumber = /[0-9]/.test(formData.password);
  const passSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(formData.password);

  const inputClass = (field) =>
    `w-full py-3.5 text-sm bg-slate-50 border ${
      errors[field] ? "border-red-400" : "border-slate-200 focus:border-[#007AFF]"
    } rounded-xl focus:bg-white focus:outline-none focus:ring-2 focus:ring-[#007AFF]/20 transition-all font-medium text-slate-900 placeholder:text-slate-400`;

  if (checkingAuth) {
    return (
      <div className="h-screen w-screen flex flex-col justify-center items-center bg-[#F8FAFC] relative overflow-hidden">
        {/* Soft background glows */}
        <div className="absolute top-1/4 left-1/3 w-96 h-96 bg-blue-100/60 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/4 right-1/3 w-96 h-96 bg-indigo-100/40 rounded-full blur-3xl pointer-events-none" />

        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.25 }}
          className="flex flex-col items-center text-center px-6 relative z-10"
        >
          {/* Animated Logo Card */}
          <motion.div
            animate={{ 
              scale: [1, 1.03, 1],
              opacity: [0.95, 1, 0.95]
            }}
            transition={{ 
              duration: 2, 
              repeat: Infinity, 
              ease: "easeInOut" 
            }}
            className="p-5 bg-white rounded-3xl shadow-[0_12px_40px_rgba(0,122,255,0.08)] border border-slate-100 mb-6"
          >
            <img
              src="/logo.png"
              alt="Americar"
              className="h-10 w-auto object-contain"
            />
          </motion.div>

          {/* Modern Spinner & Status */}
          <div className="flex items-center gap-2.5 mb-2">
            <div className="w-4 h-4 border-2 border-[#007AFF]/25 border-t-[#007AFF] rounded-full animate-spin" />
            <span className="text-sm font-bold text-slate-800 tracking-tight">
              Verifying your session...
            </span>
          </div>

          <p className="text-xs text-slate-400 font-medium">
            Checking credentials and opening your portal.
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="h-screen w-screen flex overflow-hidden bg-white">

      {/* ── LEFT: Brand Panel with Animated Cards ── */}
      <div className="hidden lg:flex w-[40%] h-full flex-col justify-between relative overflow-hidden bg-[#EEF5FF] px-14 py-14">

        {/* Soft background glows */}
        <div className="absolute top-[-80px] right-[-80px] w-[360px] h-[360px] rounded-full bg-[#007AFF]/10 blur-[90px] pointer-events-none" />
        <div className="absolute bottom-[-60px] left-[-60px] w-[280px] h-[280px] rounded-full bg-indigo-200/40 blur-[70px] pointer-events-none" />

        {/* Dot grid */}
        <div
          className="absolute inset-0 opacity-[0.3] pointer-events-none"
          style={{
            backgroundImage: "radial-gradient(circle, #93c5fd 1px, transparent 1px)",
            backgroundSize: "28px 28px",
          }}
        />

        {/* Logo */}
        <div className="relative z-10">
          <Link to="/">
            <img
              src="/logo.png"
              alt="Americar"
              className="h-9 w-auto object-contain"
            />
          </Link>
        </div>

        {/* ── Animated Cards Composition ── */}
        <div className="relative z-10 flex flex-col items-center justify-center gap-3 flex-1 py-10">

          {/* Card 1 — New Booking */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, -8, 0] }}
            transition={{ opacity: { duration: 0.5, delay: 0.1 }, y: { duration: 4, repeat: Infinity, ease: "easeInOut", delay: 0 } }}
            className="w-full max-w-[280px] bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,122,255,0.08)] border border-white/80 p-4 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center shrink-0">
              <Bell className="text-[#007AFF]" style={{ width: 18, height: 18 }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-800 truncate">New Booking</p>
              <p className="text-[11px] text-slate-400 truncate">Ford F-150 · Tire Rotation</p>
            </div>
            <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full shrink-0">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse inline-block" />
              Live
            </span>
          </motion.div>

          {/* Card 2 — Revenue */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, -8, 0] }}
            transition={{ opacity: { duration: 0.5, delay: 0.25 }, y: { duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 0.8 } }}
            className="w-full max-w-[280px] bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,122,255,0.08)] border border-white/80 p-4 self-end"
          >
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide">This Week</p>
                <p className="text-lg font-bold text-slate-900 mt-0.5">$4,280</p>
              </div>
              <div className="w-9 h-9 rounded-xl bg-emerald-50 flex items-center justify-center">
                <TrendingUp className="text-emerald-500" style={{ width: 16, height: 16 }} />
              </div>
            </div>
            <div className="flex items-end gap-1 h-8">
              {[40, 65, 45, 80, 55, 90, 72].map((h, i) => (
                <motion.div
                  key={i}
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ duration: 0.4, delay: 0.4 + i * 0.07, ease: "easeOut" }}
                  style={{ height: `${h}%`, transformOrigin: "bottom" }}
                  className={`flex-1 rounded-sm ${i === 5 ? "bg-[#007AFF]" : "bg-blue-100"}`}
                />
              ))}
            </div>
            <p className="text-[10px] text-emerald-600 font-semibold mt-2">↑ 18% vs last week</p>
          </motion.div>

          {/* Card 3 — Service Complete */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, -8, 0] }}
            transition={{ opacity: { duration: 0.5, delay: 0.4 }, y: { duration: 3.8, repeat: Infinity, ease: "easeInOut", delay: 1.6 } }}
            className="w-full max-w-[280px] bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,122,255,0.08)] border border-white/80 p-4 flex items-center gap-3"
          >
            <div className="w-10 h-10 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0">
              <CheckCircle className="text-emerald-500" style={{ width: 18, height: 18 }} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-slate-800">Service Complete</p>
              <p className="text-[11px] text-slate-400 truncate">BMW 3 Series · Full Service</p>
            </div>
            <span className="text-[10px] font-semibold text-slate-400 shrink-0">Just now</span>
          </motion.div>

          {/* Card 4 — Jobs Queued */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: [0, -6, 0] }}
            transition={{ opacity: { duration: 0.5, delay: 0.55 }, y: { duration: 5, repeat: Infinity, ease: "easeInOut", delay: 2.2 } }}
            className="w-full max-w-[240px] bg-white rounded-2xl shadow-[0_4px_24px_rgba(0,122,255,0.08)] border border-white/80 p-3.5 flex items-center gap-3 self-start"
          >
            <div className="w-8 h-8 rounded-xl bg-violet-50 flex items-center justify-center shrink-0">
              <Wrench className="text-violet-500" style={{ width: 14, height: 14 }} />
            </div>
            <div className="min-w-0">
              <p className="text-[11px] font-semibold text-slate-800">3 jobs queued</p>
              <p className="text-[10px] text-slate-400">Bay 2 · Ready for dispatch</p>
            </div>
          </motion.div>

        </div>

        {/* Bottom text */}
        <div className="relative z-10">
          <span className="inline-block text-[#007AFF] text-[10px] font-bold tracking-[0.15em] uppercase mb-3 bg-white/70 border border-blue-100 px-2.5 py-1 rounded-full backdrop-blur-sm">
            Partner Onboarding
          </span>
          <h1 className="text-slate-800 text-2xl font-bold tracking-tight leading-[1.3]">
            Start growing today.
          </h1>
          <p className="text-slate-400 text-sm mt-1.5">Join 1,200+ automotive businesses on the platform.</p>
        </div>

      </div>

      {/* ── RIGHT: Registration Form ── */}
      <div className="flex-1 h-full overflow-y-auto bg-white">
        <div className="min-h-full flex flex-col justify-center items-center px-8 sm:px-16 lg:px-20 xl:px-28 py-14">

          {/* Mobile logo */}
          <div className="lg:hidden mb-10 self-start">
            <Link to="/">
              <img
                src="/logo.png"
                alt="Americar"
                className="h-9 w-auto object-contain"
              />
            </Link>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
            className="w-full max-w-md"
          >
            {/* Heading */}
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-slate-900 tracking-tight mb-1.5">Create account</h2>
              <p className="text-slate-400 text-sm">Register your business and get set up in minutes.</p>
            </div>

            {/* General error */}
            {errors.general && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3.5 rounded-xl mb-6 text-sm font-medium flex items-start gap-3"
              >
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5 text-red-500" />
                <span>{errors.general}</span>
              </motion.div>
            )}

            <form onSubmit={handleSignup} className="space-y-4">

              {/* Name row */}
              <div className="grid grid-cols-2 gap-3.5">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">First Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      placeholder="John"
                      value={formData.firstName}
                      onChange={(e) => handleInputChange("firstName", e.target.value)}
                      onBlur={(e) => handleInputBlur("firstName", e.target.value)}
                      className={`${inputClass("firstName")} pl-10 pr-3`}
                      disabled={loading}
                    />
                  </div>
                  {errors.firstName && <span className="text-[11px] text-red-500 font-medium mt-1 block">{errors.firstName}</span>}
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Last Name</label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <User className="w-4 h-4" />
                    </div>
                    <input
                      type="text"
                      placeholder="Doe"
                      value={formData.lastName}
                      onChange={(e) => handleInputChange("lastName", e.target.value)}
                      onBlur={(e) => handleInputBlur("lastName", e.target.value)}
                      className={`${inputClass("lastName")} pl-10 pr-3`}
                      disabled={loading}
                    />
                  </div>
                  {errors.lastName && <span className="text-[11px] text-red-500 font-medium mt-1 block">{errors.lastName}</span>}
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Business Email</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Mail className="w-4 h-4" />
                  </div>
                  <input
                    type="email"
                    placeholder="john@autocare.com"
                    value={formData.email}
                    onChange={(e) => handleInputChange("email", e.target.value)}
                    onBlur={(e) => handleInputBlur("email", e.target.value)}
                    className={`${inputClass("email")} pl-10 pr-4`}
                    disabled={loading}
                  />
                </div>
                {errors.email && <span className="text-[11px] text-red-500 font-medium mt-1 block">{errors.email}</span>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Mobile Phone</label>
                <div className={`flex rounded-xl bg-slate-50 border ${errors.mobile ? "border-red-400" : "border-slate-200"} focus-within:bg-white focus-within:ring-2 focus-within:ring-[#007AFF]/20 focus-within:border-[#007AFF] transition-all overflow-hidden`}>
                  <input
                    type="text"
                    value={formData.countryCode}
                    onChange={(e) => handleInputChange("countryCode", e.target.value)}
                    className="w-14 px-2 py-3.5 text-xs text-slate-700 font-bold bg-slate-100/80 border-r border-slate-200 text-center focus:outline-none"
                    placeholder="+1"
                  />
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                      <Phone className="w-4 h-4" />
                    </div>
                    <input
                      type="tel"
                      placeholder="10-digit number"
                      value={formData.mobile}
                      onChange={(e) => handleInputChange("mobile", e.target.value)}
                      onBlur={(e) => handleInputBlur("mobile", e.target.value)}
                      className="w-full pl-10 pr-3 py-3.5 text-sm bg-transparent border-0 focus:outline-none font-medium text-slate-900"
                      maxLength="10"
                      disabled={loading}
                    />
                  </div>
                </div>
                {errors.mobile && <span className="text-[11px] text-red-500 font-medium mt-1 block">{errors.mobile}</span>}
              </div>

              {/* Password */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••••••"
                    value={formData.password}
                    onChange={(e) => handleInputChange("password", e.target.value)}
                    onBlur={(e) => handleInputBlur("password", e.target.value)}
                    className={`${inputClass("password")} pl-10 pr-11`}
                    disabled={loading}
                  />
                  <button type="button" onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors">
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.password && <span className="text-[11px] text-red-500 font-medium mt-1 block">{errors.password}</span>}
                {formData.password && (
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {[
                      { met: passLength, label: "8+ chars" },
                      { met: passUpper, label: "Uppercase" },
                      { met: passNumber, label: "Number" },
                      { met: passSpecial, label: "Special" },
                    ].map(({ met, label }) => (
                      <span key={label} className={`text-[10px] font-semibold px-2 py-0.5 rounded-md flex items-center gap-1 ${met ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                        {met ? <Check className="w-2.5 h-2.5 text-emerald-600" /> : "·"} {label}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Confirm Password</label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-slate-400">
                    <Lock className="w-4 h-4" />
                  </div>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••••••"
                    value={formData.confirmPassword}
                    onChange={(e) => handleInputChange("confirmPassword", e.target.value)}
                    onBlur={(e) => handleInputBlur("confirmPassword", e.target.value)}
                    className={`${inputClass("confirmPassword")} pl-10 pr-11`}
                    disabled={loading}
                  />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3.5 flex items-center text-slate-400 hover:text-slate-600 transition-colors">
                    {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {errors.confirmPassword && <span className="text-[11px] text-red-500 font-medium mt-1 block">{errors.confirmPassword}</span>}
              </div>

              {/* Terms */}
              <p className="text-[11px] text-slate-400 leading-relaxed pt-1">
                By registering you agree to Americar's{" "}
                <Link to="/termsOfService" className="text-[#007AFF] underline">Terms</Link> and{" "}
                <Link to="/privacyPolicy" className="text-[#007AFF] underline">Privacy Policy</Link>.
              </p>

              {/* CTA */}
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 px-6 bg-[#007AFF] hover:bg-blue-600 active:scale-[0.99] text-white rounded-xl font-bold text-sm shadow-md shadow-blue-500/20 hover:shadow-blue-500/30 transition-all duration-150 flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed mt-2"
              >
                {loading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>Creating account...</span>
                  </>
                ) : (
                  <>
                    <span>Complete Registration</span>
                    <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>

            </form>

            {/* Footer */}
            <div className="mt-7 pt-6 border-t border-slate-100 flex items-center justify-between text-sm">
              <span className="flex items-center gap-1.5 text-slate-400 font-medium text-xs">
                <ShieldCheck className="w-4 h-4 text-emerald-500" /> Business verified
              </span>
              <Link to="/login" className="text-[#007AFF] font-bold text-sm hover:underline">
                Already registered? Sign in →
              </Link>
            </div>

          </motion.div>
        </div>
      </div>

    </div>
  );
};

export default SignUp;
